/**
 * CenoX Business, for somebody who does not have a business in it yet.
 *
 * This is where Business onboarding starts — and the ONLY place it starts.
 * Signing up for CenoX makes an identity; it does not make a shop. A person
 * reaches this screen by deliberately opening CenoX Business, and is then
 * offered the two honest ways in: create a business, or join one somebody
 * else already runs.
 *
 * Joining is by invitation, because membership is granted by the business and
 * never claimed by the person. So "Join" does not search for businesses — it
 * explains where the invitation comes from and accepts the link if they have
 * it to hand. Somebody invited as staff who lands here must never be steered
 * into creating a second business of their own.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, Store, UserPlus } from 'lucide-react';
import { CenoxShell } from '../../layouts/CenoxShell';
import { Button, Input } from '../../components/ui';

/**
 * Pull an invitation token out of whatever was pasted.
 *
 * People paste the whole link from the email, or just the end of it; both
 * work. Only the token is kept, and it is navigated to on this origin — a
 * pasted link to somewhere else is not followed anywhere.
 */
export function invitationToken(input: string): string | null {
  const text = input.trim();
  if (!text) return null;
  const match = text.match(/(?:\/join\/|\/accept-invite\/)([A-Za-z0-9_-]+)/);
  if (match) return match[1] ?? null;
  return /^[A-Za-z0-9_-]{16,}$/.test(text) ? text : null;
}

export function BusinessStartPage(): ReactNode {
  const navigate = useNavigate();
  // `?join=1` — arriving from a "Join / Accept invite" button elsewhere.
  const [params] = useSearchParams();
  const [joining, setJoining] = useState(params.get('join') === '1');
  const [link, setLink] = useState('');
  const [error, setError] = useState<string | null>(null);

  const join = (event: FormEvent): void => {
    event.preventDefault();
    const token = invitationToken(link);
    if (!token) {
      setError('That does not look like an invitation link. Copy the whole link from the email.');
      return;
    }
    navigate(`/join/${token}`);
  };

  return (
    <CenoxShell>
      <div className="mx-auto max-w-2xl">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="size-4" aria-hidden />
          CenoX Home
        </Link>

        <div className="mt-6 flex items-center gap-3">
          <span className="flex size-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 text-white">
            <Building2 className="size-5" aria-hidden />
          </span>
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">CenoX Business</h1>
            <p className="text-sm text-slate-500">
              Sales, stock, staff, locations and reports for the business you run.
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => navigate('/business/onboarding')}
            className="flex flex-col items-start rounded-2xl border border-slate-200 bg-white p-5 text-left transition-shadow hover:border-brand-300 hover:shadow-md"
          >
            <Store className="size-5 text-brand-600" aria-hidden />
            <span className="mt-3 text-sm font-semibold text-slate-900">Create a business</span>
            <span className="mt-1 text-sm text-slate-500">
              Set up your business, how it operates and your first location. You will own it.
            </span>
          </button>

          <button
            type="button"
            onClick={() => setJoining(true)}
            aria-expanded={joining}
            className="flex flex-col items-start rounded-2xl border border-slate-200 bg-white p-5 text-left transition-shadow hover:border-brand-300 hover:shadow-md"
          >
            <UserPlus className="size-5 text-brand-600" aria-hidden />
            <span className="mt-3 text-sm font-semibold text-slate-900">Join a business</span>
            <span className="mt-1 text-sm text-slate-500">
              Work in a business someone else runs. You need an invitation from its owner.
            </span>
          </button>
        </div>

        {joining && (
          <form
            onSubmit={join}
            className="mt-4 rounded-2xl border border-slate-200 bg-white p-5"
            noValidate
          >
            <h2 className="text-sm font-semibold text-slate-900">Join with your invitation</h2>
            <p className="mt-1 text-sm text-slate-500">
              The owner or a manager invites you from their Staff screen, and you receive a link by
              email. Open it, or paste it here. If you have no invitation yet, ask them to send one
              — there is nothing to create on your side.
            </p>
            <div className="mt-4">
              <Input
                label="Invitation link"
                name="invitation"
                value={link}
                onChange={(e) => {
                  setLink(e.target.value);
                  setError(null);
                }}
                placeholder="https://cenox.ai/join/…"
                error={error ?? undefined}
              />
            </div>
            <Button type="submit" className="mt-4" disabled={!link.trim()}>
              Continue
            </Button>
          </form>
        )}
      </div>
    </CenoxShell>
  );
}
